import React, { useEffect, useMemo, useState } from "react";
import { Button, Carousel, FileInput, Modal } from "flowbite-react";
import Compressor from "compressorjs";
import {
  HiOutlineCloudUpload,
  HiOutlineStar,
  HiOutlineTrash,
  HiStar,
} from "react-icons/hi";
import { useDispatch } from "react-redux";
import { deletarFotoAction, uploadFotosAction } from "../../../actions/foto";
import { showNotification } from "../../../actions/notification";
import { toggleFotoCapa } from "../../../api/fotos";

const ordenarFotosComCapaPrimeiro = (fotos = []) => {
  return [...fotos].sort((a, b) => {
    if (a.e_capa && !b.e_capa) return -1;
    if (!a.e_capa && b.e_capa) return 1;
    return new Date(a.createdAt || 0) - new Date(b.createdAt || 0);
  });
};

const UploadFotosProduto = ({
  produto,
  onUploadSuccess,
  onDeleteSuccess,
  onCoverChange,
  canUpload = true,
}) => {
  const [fotos, setFotos] = useState([]);
  const [modalDeleteOpen, setModalDeleteOpen] = useState(false);
  const [fotoSelecionada, setFotoSelecionada] = useState(null);
  const [updatingCoverId, setUpdatingCoverId] = useState(null);
  const [uploading, setUploading] = useState(false);
  const dispatch = useDispatch();

  const podeFazerUpload = !!produto?.id && canUpload && !uploading;
  const fotosExistentes = ordenarFotosComCapaPrimeiro(produto?.fotos || []);

  const previews = useMemo(
    () => fotos.map((foto) => ({ file: foto, url: URL.createObjectURL(foto) })),
    [fotos]
  );

  useEffect(() => {
    return () => {
      previews.forEach((preview) => URL.revokeObjectURL(preview.url));
    };
  }, [previews]);

  const isValidImage = (file) => {
    return (
      file.type.startsWith("image/") && /\.(jpe?g|png|webp)$/i.test(file.name)
    );
  };

  const handleFileChange = (event) => {
    const files = Array.from(event.target.files || []);

    if (!podeFazerUpload) return;

    if (files.length + fotos.length > 10) {
      dispatch(
        showNotification({
          message: "Voce pode enviar no maximo 10 fotos por vez.",
          color: "warning",
        })
      );
      return;
    }

    const validFiles = files.filter(isValidImage);
    if (validFiles.length !== files.length) {
      dispatch(
        showNotification({
          message: "Alguns arquivos foram rejeitados por nao serem imagens validas.",
          color: "warning",
        })
      );
    }

    setFotos((prev) => [...prev, ...validFiles]);
  };

  const processImages = async (files) => {
    return Promise.all(
      files.map((file) => {
        return new Promise((resolve, reject) => {
          const img = new Image();
          img.src = URL.createObjectURL(file);
          img.onload = () => {
            const { width, height } = img;
            if (width <= 1200 && height <= 1200) {
              resolve(file);
            } else {
              new Compressor(file, {
                maxWidth: width >= height ? 1200 : undefined,
                maxHeight: height > width ? 1200 : undefined,
                quality: 0.8,
                success: (compressedFile) => resolve(compressedFile),
                error: (err) => reject(err),
              });
            }
          };
          img.onerror = () => reject("Erro ao carregar a imagem.");
        });
      })
    );
  };

  const handleUpload = async () => {
    if (!podeFazerUpload) {
      dispatch(
        showNotification({
          message: "Crie o produto antes de adicionar fotos.",
          color: "warning",
        })
      );
      return;
    }

    if (fotos.length === 0) {
      dispatch(
        showNotification({
          message: "Selecione ao menos uma foto para enviar.",
          color: "warning",
        })
      );
      return;
    }

    if (!produto?.descricao) {
      dispatch(
        showNotification({
          message: "Preencha a descricao do produto antes de enviar fotos.",
          color: "warning",
        })
      );
      return;
    }

    try {
      setUploading(true);
      const processedFotos = await processImages(fotos);
      const fotosEnviadas = await dispatch(
        uploadFotosAction(produto.id, produto.descricao, processedFotos)
      );
      setFotos([]);
      if (onUploadSuccess) {
        onUploadSuccess(fotosEnviadas || []);
      }
    } catch (error) {
      console.error("Erro ao processar/enviar imagens:", error);
    } finally {
      setUploading(false);
    }
  };

  const abrirModalDelete = (foto) => {
    if (!canUpload) return;
    setFotoSelecionada(foto);
    setModalDeleteOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!fotoSelecionada?.id || !canUpload) return;

    try {
      await dispatch(deletarFotoAction(fotoSelecionada.id));
      if (onDeleteSuccess) {
        onDeleteSuccess(fotoSelecionada.id);
      }
      setModalDeleteOpen(false);
      setFotoSelecionada(null);
    } catch (error) {
      console.error("Erro ao excluir foto:", error);
    }
  };

  const handleToggleCapa = async (foto) => {
    if (!canUpload || !foto?.id) return;

    try {
      setUpdatingCoverId(foto.id);
      const { data } = await toggleFotoCapa(foto.id);

      if (onCoverChange) {
        onCoverChange(data?.foto || { id: foto.id, e_capa: !foto.e_capa });
      }

      dispatch(
        showNotification({
          message: data?.message || "Capa atualizada com sucesso.",
          color: "success",
        })
      );
    } catch (error) {
      dispatch(
        showNotification({
          message: error?.message || "Erro ao atualizar foto de capa.",
          color: "failure",
        })
      );
    } finally {
      setUpdatingCoverId(null);
    }
  };

  return (
    <div className="space-y-4">
      <div>
        <h3 className="text-sm font-semibold text-gray-700">Fotos do Produto</h3>
        <p className="text-xs text-gray-500 mt-1">
          Faca upload de ate 10 fotos. A estrela define qual foto sera a capa no catalogo.
        </p>
      </div>

      <FileInput
        multiple
        accept="image/png, image/jpeg, image/webp"
        onChange={handleFileChange}
        disabled={!podeFazerUpload}
      />

      {!canUpload && (
        <p className="text-sm text-gray-600 italic">
          Upload, exclusao e capa bloqueados enquanto o plano estiver inativo.
        </p>
      )}

      {canUpload && !podeFazerUpload && (
        <p className="text-sm text-gray-600 italic">
          Salve o produto primeiro para habilitar o upload de fotos.
        </p>
      )}

      {previews.length > 0 && (
        <div>
          <p className="text-xs text-gray-500 mb-2">Pre-visualizacao do envio</p>
          <div className="grid grid-cols-3 gap-2">
            {previews.map((preview, index) => (
              <img
                key={`${preview.file.name}-${index}`}
                src={preview.url}
                alt={`Preview ${index + 1}`}
                className="w-full h-24 object-cover rounded-md border"
              />
            ))}
          </div>
        </div>
      )}

      <Button
        color="blue"
        onClick={handleUpload}
        disabled={!podeFazerUpload || fotos.length === 0}
        isProcessing={uploading}
      >
        <HiOutlineCloudUpload className="w-5 h-5 mr-2" />
        {uploading ? "Processando fotos..." : "Enviar Fotos"}
      </Button>

      <div className="border rounded-lg p-3 bg-gray-50">
        <p className="text-xs text-gray-500 mb-2">
          Fotos ja cadastradas ({fotosExistentes.length})
        </p>

        {fotosExistentes.length > 0 ? (
          <>
            <div className="h-56 rounded-md overflow-hidden bg-white">
              <Carousel
                indicators={fotosExistentes.length > 1}
                slide={fotosExistentes.length > 1}
              >
                {fotosExistentes.map((foto) => (
                  <img
                    key={foto.id}
                    src={`${process.env.REACT_APP_UPLOADS_URL}${foto.url}`}
                    alt={produto?.descricao || "Foto do produto"}
                    className="w-full h-full object-cover"
                    loading="lazy"
                    decoding="async"
                  />
                ))}
              </Carousel>
            </div>

            <div className="mt-2 grid grid-cols-4 sm:grid-cols-5 gap-2">
              {fotosExistentes.map((foto) => (
                <div key={`thumb-${foto.id}`} className="relative group">
                  <img
                    src={`${process.env.REACT_APP_UPLOADS_URL}${foto.url}`}
                    alt="Miniatura"
                    className="w-full h-16 rounded object-cover border bg-white"
                    loading="lazy"
                    decoding="async"
                  />

                  <button
                    type="button"
                    className={`absolute top-1 left-1 p-1 rounded-full border ${
                      foto.e_capa
                        ? "bg-yellow-100 text-yellow-700 border-yellow-300"
                        : "bg-white/90 text-gray-500 border-gray-200"
                    } ${canUpload ? "hover:bg-yellow-50" : "opacity-40 cursor-not-allowed"}`}
                    onClick={() => handleToggleCapa(foto)}
                    disabled={!canUpload || updatingCoverId === foto.id}
                    aria-label="Definir foto de capa"
                    title={foto.e_capa ? "Remover capa" : "Definir como capa"}
                  >
                    {foto.e_capa ? (
                      <HiStar className="w-4 h-4" />
                    ) : (
                      <HiOutlineStar className="w-4 h-4" />
                    )}
                  </button>

                  <button
                    type="button"
                    className={`absolute top-1 right-1 p-1 rounded-full bg-white/90 text-red-600 border border-red-200 ${
                      canUpload
                        ? "opacity-100 hover:bg-red-50"
                        : "opacity-40 cursor-not-allowed"
                    }`}
                    onClick={() => abrirModalDelete(foto)}
                    disabled={!canUpload}
                    aria-label="Excluir foto"
                    title="Excluir foto"
                  >
                    <HiOutlineTrash className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          </>
        ) : (
          <p className="text-sm text-gray-500">Nenhuma foto cadastrada ainda.</p>
        )}
      </div>

      <Modal
        show={modalDeleteOpen}
        size="md"
        onClose={() => setModalDeleteOpen(false)}
      >
        <Modal.Header>Confirmar Exclusao</Modal.Header>
        <Modal.Body>
          {fotoSelecionada && (
            <div className="flex flex-col items-center">
              <img
                src={`${process.env.REACT_APP_UPLOADS_URL}${fotoSelecionada.url}`}
                alt="Thumb da foto"
                className="w-40 h-auto rounded-lg shadow-md mb-4"
              />
              <p className="text-gray-700 text-center">
                Tem certeza que deseja excluir esta foto?
              </p>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button color="failure" onClick={handleConfirmDelete}>
            SIM, Excluir
          </Button>
          <Button color="gray" onClick={() => setModalDeleteOpen(false)}>
            Cancelar
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default UploadFotosProduto;
